import { BaseApp, AppEvents, Constance, BarcodeAuthentication } from "../core/app/index";
import FortumDatabase from "./db/index";
import OrderDetailView from "./views/decontamination/order/detail/index";
import ScanOrderView from "./views/decontamination/order/scan/index";
import MainMenuView from "./views/mainmenu/index";
import ActionBar from "../core/action-bar/index";
import { HomeButton, LogoutButton, SyncButton, InfoButton } from "../core/action-bar/items/index";
import ViewProcessorManager from "../core/view/processor/index";
import BBrowser from "../core/bbclient/browser";
import Router from "../core/router/index";
import OrderItemDetailView from "./views/decontamination/orderItem/detail/index";
import FlipcardViewProcessor from "../core/view/processor/flipcard";
import { ShelvePackagesView } from "./views/package_tracking/receive/shelve_packages";
import { ScanReceivePackagesView } from "./views/package_tracking/receive/index";
import { CollectForDeliveryView } from "./views/package_tracking/collect_for_delivery";
import { DeliveriesListView } from "./views/package_tracking/list";
import { BarcodeAuthenticationButton } from './action-bar-items';
import BarcodeAuthenticationModal from "./modals/barcode-authentication";
import { isBBSyncEnabled } from "../core/helpers/offline-sync";
import { Customer, CustomerLike } from "./db/models/customer";
import { UserAccount, User } from "../core/db/models";
/**
 * Base application for all Fortum client applications. 
 * Contains shared functionality that doesn't concern the BaseApp class.
 */
export class FortumApp extends BaseApp
{
    public db: FortumDatabase;
    public constance: FortumConstance;
    
    protected initDatabase(): PromiseLike<void>
    {
        this.db = new FortumDatabase(this.settings.clientSync.databaseName);
        return super.initDatabase();
    }

    public configureRouter(router: Router)
    {
        super.configureRouter(router);
 
        router.add(/^\/order\/([a-fA-F0-9-]+)\/detail\/?$/, OrderDetailView);
        router.add(/^\/order\/([a-fA-F0-9-]+)\/item\/([0-9]+)\/detail\/?$/, OrderItemDetailView);
        router.add(/^\/order\/scan\/?$/, ScanOrderView);

        router.add(/^\/package_tracking\/delivery\/collect\/?$/, CollectForDeliveryView);
        router.add(/^\/package_tracking\/receive\/?$/, ScanReceivePackagesView);
        router.add(/^\/package_tracking\/shelve\/?$/, ShelvePackagesView);
        router.add(/^\/package_tracking\/delivery\/list\/?$/, DeliveriesListView);
        router.add(/^\/?$/, MainMenuView);
    }

    public configureActionbar(actionBar: ActionBar) 
    {
        super.configureActionbar(actionBar);

        this.header.defaultText = '...';
        
        actionBar.add(InfoButton);
        actionBar.add(HomeButton);

        const addRemainingActionBarItems = async () =>
        {   
            if (this.barcodeAuthentication.isEnabled) {
                actionBar.add(BarcodeAuthenticationButton);
            }
            else {
                actionBar.add(LogoutButton);
            }
            actionBar.add(SyncButton);
        };

        if (this.isAuthenticated) {
            addRemainingActionBarItems();
        }
        
        this.on(AppEvents.AUTHENTICATED, addRemainingActionBarItems);
    }

    public async run() {
        this.barcodeAuthentication.isEnabled = (await isBBSyncEnabled());
        await super.run();

        if (this.barcodeAuthentication.isEnabled && !this.barcodeAuthentication.user) {
            this.modals.show(new BarcodeAuthenticationModal());
        } else if (this.barcodeAuthentication.isEnabled) {
            const [customer, account, user] = await Promise.all([
                this.db.Customer.where('id').equals(this.barcodeAuthentication.user.customer_id).first(),
                this.db.UserAccount.where('id').equals(this.barcodeAuthentication.user.account_id).first(),
                this.db.User.where('id').equals(this.barcodeAuthentication.user.user_id).first(),
            ]);

            if (!customer || !account || !user) {
                localStorage.removeItem('barcode-authentication');
                location.reload();
            } else {
                const authUser = this.writeBarcodeAuthentication({customer, user, account});
                this.toasts.show(this.pgettext('barcode-authentication', 'Authenticated as {username}!', {username: authUser.display_name}));
            }
        }
    }

    public configureViewProcessors(processors: ViewProcessorManager)
    {
        super.configureViewProcessors(processors);

        // processors.add(TabControlViewProcessor);
        processors.add(FlipcardViewProcessor);
    }

    public writeBarcodeAuthentication(data: {customer: CustomerLike, user: User, account: UserAccount}) {
        const authUser = <BarcodeAuthentication['user']>{
            person_number: data.customer.number,
            username: data.user.username,
            first_name: data.customer.first_name,
            last_name: data.customer.last_name,
            account_id: data.customer.user_account_id,
            user_id: data.account.user_id,
            customer_id: data.customer.id,
            nfc_token: data.customer.nfc_token,
            location_id: data.customer.location_id,
            display_name: `${data.customer.first_name} ${data.customer.last_name}`,
        };
        localStorage.setItem('barcode-authentication', JSON.stringify(authUser));
        return authUser;
    }
}

export interface FortumConstance extends Constance
{

}

export const app = new FortumApp();
export {BBrowser as Browser};